import { useMytaverseEvent } from '@core/dashboard/providers';

import { useEditorMode } from './EditorModeContext';
import { useCanUseMapEditor } from './useCanUseMapEditor';

/**
 * Whether the author is standing inside the layout rather than looking down at the plan.
 *
 * Driven off `EDITOR_MODE_STATE` rather than off what this hub last sent, which is the whole reason
 * that message exists: something other than the web can move the author — a console command in a QA
 * session, or another feature claiming the camera out from under the plan view — and a control drawn
 * from a local flag would then be offering a way out of a view they are not in.
 *
 * The local request is the fallback for a build that sends no state at all, and only for that. Read
 * the order below literally: a report, however it reads, outranks what this side asked for. The
 * fallback exists because the two halves of the walk are not gated alike — **Take me there** is a
 * control and is always drawn, while the way back is not — so without it such a build lets an author
 * walk in and offers nothing that walks back out.
 *
 * Lives here rather than inside the branding bar because the bar is Editor Mode's shared chrome and
 * is drawn on every focus, while this answer belongs to one of them.
 *
 * @returns True while Unreal reports the walk view for the map-editor focus.
 */
export const useIsWalkingLayout = (): boolean => {
  const { editorModeState } = useMytaverseEvent();
  const { activeFocus, walkRequested } = useEditorMode();
  const canUseMapEditor = useCanUseMapEditor();

  // `enabled` decides, exactly as it does on the wire: a state that has left the editor is not a
  // walk, whatever view it names. Only where no state has arrived at all does the local request
  // answer — and it is bounded by the focus, because Unreal's editor mode is only ever on for this
  // one.
  return (
    canUseMapEditor &&
    activeFocus === 'mapEditor' &&
    (editorModeState
      ? editorModeState.enabled && editorModeState.view === 'walk'
      : walkRequested)
  );
};

export default useIsWalkingLayout;
